import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Bot, Users, Search, Plus } from 'lucide-react';
import { StatusBadge } from './StatusBadge';
import { getDepartmentFilterText, departmentsWithCategories } from '../../data/departmentConfig';

const AgentListPanel = ({
  agents,
  sortedFilteredAgents,
  selectedAgent,
  setSelectedAgent,
  searchTerm,
  setSearchTerm,
  statusFilter,
  setStatusFilter,
  roleFilter,
  setRoleFilter,
  departmentFilter,
  setDepartmentFilter,
  departments,
  uniqueDepartments,
  uniqueRoles,
  activeCount,
  categoryOrder,
  onNewAgentClick
}) => {
  const isDepartmentWithCategories = departmentsWithCategories.includes(departmentFilter);

  return (
    <Card 
      className="lg:col-span-1 flex flex-col overflow-hidden border-2 border-primary/30"
      style={{
        boxShadow: '0 0 15px rgba(59, 130, 246, 0.3), 0 0 30px rgba(59, 130, 246, 0.15)'
      }}
    >
      <CardHeader className="pb-2 shrink-0">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <Users className="h-4 w-4" />
            Agents
          </CardTitle>
          <div className="flex gap-1.5">
            {departmentFilter !== 'all' && (
              <Badge className="bg-primary/20 text-primary text-[10px]">
                {departments.find(d => d.id === departmentFilter)?.active}/{departments.find(d => d.id === departmentFilter)?.total} Active
              </Badge>
            )}
            <Badge variant="secondary" className="text-[10px]">{activeCount} Total</Badge>
          </div>
        </div>
        
        {/* Filters */}
        <div className="space-y-2 pt-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search agents..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9 h-8 text-sm"
            />
          </div>
          {/* Department Filter */}
          <Select value={departmentFilter} onValueChange={setDepartmentFilter}>
            <SelectTrigger className="w-full h-8 text-xs">
              <SelectValue placeholder="Department" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Departments</SelectItem>
              {uniqueDepartments.map(dept => (
                <SelectItem key={dept} value={dept}>
                  {getDepartmentFilterText(dept)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="flex gap-2">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="flex-1">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="paused">Paused</SelectItem>
                <SelectItem value="training">In Training</SelectItem>
                <SelectItem value="offline">Offline</SelectItem>
              </SelectContent>
            </Select>
            <Select value={roleFilter} onValueChange={setRoleFilter}>
              <SelectTrigger className="flex-1">
                <SelectValue placeholder="Role" />
              </SelectTrigger>
              <SelectContent className="max-h-80">
                <SelectItem value="all">All Roles</SelectItem>
                {uniqueRoles.map(role => (
                  <SelectItem key={role} value={role}>{role}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </CardHeader>
      
      <CardContent className="flex-1 overflow-y-auto pt-0">
        {sortedFilteredAgents.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <Bot className="h-12 w-12 text-muted-foreground/50 mb-4" />
            <p className="font-medium text-muted-foreground">No agents created yet</p>
            <p className="text-sm text-muted-foreground/70 mt-1">
              Click &apos;+ New Agent&apos; to create your first AI employee.
            </p>
            <Button className="mt-4" onClick={onNewAgentClick}>
              <Plus className="h-4 w-4 mr-2" />
              Create Agent
            </Button>
          </div>
        ) : (
          <div className="space-y-2">
            {/* Group by category when a specific department is selected */}
            {isDepartmentWithCategories ? (
              <>
                {categoryOrder.map(category => {
                  const categoryAgents = sortedFilteredAgents.filter(a => a.category === category);
                  if (categoryAgents.length === 0) return null;
                  return (
                    <div key={category} className="mb-4">
                      <div className="flex items-center gap-2 px-2 py-1 mb-2">
                        <div className="h-1 w-1 rounded-full bg-primary" />
                        <span className="text-xs font-semibold uppercase tracking-wider text-primary">{category}</span>
                        <div className="flex-1 h-px bg-primary/20" />
                      </div>
                      {categoryAgents.map(agent => (
                        <AgentListItem
                          key={agent.id}
                          agent={agent}
                          isSelected={selectedAgent?.id === agent.id}
                          onClick={() => setSelectedAgent(agent)}
                          showDepartment={false}
                        />
                      ))}
                    </div>
                  );
                })}
              </>
            ) : (
              sortedFilteredAgents.map(agent => (
                <AgentListItem
                  key={agent.id}
                  agent={agent}
                  isSelected={selectedAgent?.id === agent.id}
                  onClick={() => setSelectedAgent(agent)}
                  showDepartment={true}
                />
              ))
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Individual Agent List Item
const AgentListItem = ({ agent, isSelected, onClick, showDepartment }) => (
  <button
    onClick={onClick}
    className={`w-full p-3 text-left rounded-lg border transition-all duration-200 hover:border-primary/50 mb-2 ${
      isSelected 
        ? 'border-primary bg-primary/5' 
        : 'border-border hover:bg-accent/50'
    }`}
  >
    <div className="flex items-start gap-3">
      <div className="h-10 w-10 rounded-full flex items-center justify-center shrink-0 bg-primary/10">
        <Bot className="h-5 w-5 text-primary" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <span className="font-medium truncate">{agent.name}</span>
          <StatusBadge status={agent.status} />
        </div>
        <p className="text-sm text-muted-foreground truncate">{agent.role}</p>
        {showDepartment ? (
          <div className="flex items-center gap-2 mt-1">
            <Badge className="bg-primary/20 text-primary text-xs px-1.5 py-0">{agent.department}</Badge>
            <p className="text-xs text-muted-foreground">
              Queue: {agent.tasksInQueue} | Today: {agent.tasksToday}
            </p>
          </div>
        ) : (
          <p className="text-xs text-muted-foreground mt-1">
            Tasks in queue: {agent.tasksInQueue} | Today: {agent.tasksToday}
          </p>
        )}
      </div>
    </div>
  </button>
);

export default AgentListPanel;